import { Container, Eyebrow, MButton, ArrowRight, MarketingPage, IMG } from "./ui";
import { breadcrumb } from "./seo";

const VALUES: { title: string; body: string }[] = [
  {
    title: "Ship the calm version",
    body: "We build software that feels serene to use. Fewer knobs, clearer states, no surprises — for patients and clinicians alike.",
  },
  {
    title: "Safety is the product",
    body: "The AI informs and triages, with clinician sign-off and emergency escalation built in. It never makes a diagnosis the machine decides alone.",
  },
  {
    title: "Remember the whole patient",
    body: "Every visit, report and vital lives in a longitudinal health journal. Care is continuous, so the next conversation always starts informed.",
  },
  {
    title: "Care should be accessible",
    body: "A thorough, unhurried visit shouldn't depend on geography or a waiting-room slot. Everyone deserves a doctor with time to listen.",
  },
];

const STEPS: { step: string; title: string; body: string }[] = [
  {
    step: "01",
    title: "Configure an AI doctor",
    body: "Choose a specialty and clinical model, then bind it to a medical persona and a lifelike video avatar — ready in minutes, no scripting marathons.",
  },
  {
    step: "02",
    title: "Patients book & upload reports",
    body: "Patients self-schedule by email and upload labs, imaging and prior notes. Our ingestion pipeline writes a structured pre-visit brief before the call.",
  },
  {
    step: "03",
    title: "Consult, summarize & sign off",
    body: "The AI doctor joins already briefed, reasons over the patient's history and live vitals, and returns a SOAP summary, triage and next steps — routed for clinician review.",
  },
];

const STACK: { name: string; what: string }[] = [
  { name: "AI video consultations", what: "Real-time, lifelike conversation that runs in the browser" },
  { name: "Clinical report ingestion", what: "Labs, imaging and notes extracted with text and vision" },
  { name: "Health journal & retrieval", what: "A longitudinal record the doctor reasons over with memory" },
  { name: "Real-time vitals", what: "HR, SpO₂, blood pressure and temperature stream into the room" },
  { name: "Transactional email", what: "Booking, reminders and visit summaries that land" },
  { name: "Stripe billing", what: "Simple, pay-as-you-go per completed consultation" },
  { name: "Cloudflare edge", what: "A fast global network with managed Postgres" },
];

export function About() {
  return (
    <MarketingPage
      seo={{
        title: "About — WonderfulDoctor",
        description:
          "WonderfulDoctor is a pay-as-you-go AI telehealth platform from Wonderful Agent. An AI video doctor reads each patient's reports, reacts to live vitals, and routes every visit to a licensed clinician for sign-off.",
        path: "/about",
        jsonLd: breadcrumb([
          { name: "Home", path: "/" },
          { name: "About", path: "/about" },
        ]),
      }}
    >
      {/* Header */}
      <section className="relative overflow-hidden">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-0 h-80"
          style={{
            background: "radial-gradient(60% 60% at 50% 0%, rgba(244,201,155,0.5) 0%, rgba(244,201,155,0) 70%)",
          }}
        />
        <Container className="relative pt-20 pb-12 text-center sm:pt-28">
          <Eyebrow>About us</Eyebrow>
          <h1 className="mx-auto mt-7 max-w-3xl text-balance text-5xl font-bold leading-[1.05] tracking-tight sm:text-6xl">
            Making care{" "}
            <span className="font-display italic font-normal text-warm-accent">informed, safe and accessible</span>.
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg text-warm-muted">
            WonderfulDoctor is a pay-as-you-go AI telehealth platform — a Wonderful Agent product. Every patient
            deserves a doctor who has actually read their chart and has the time to listen, and a licensed clinician
            stays in the loop so no one is ever harmed by an AI acting alone.
          </p>
          <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <MButton to="/careers">
              Join the team <ArrowRight />
            </MButton>
            <MButton to="/platform" variant="outline">
              Explore the platform
            </MButton>
          </div>
        </Container>
      </section>

      {/* Our mission */}
      <section className="py-24">
        <Container>
          <div className="mx-auto max-w-2xl">
            <Eyebrow>Our mission</Eyebrow>
            <h2 className="mt-5 text-4xl font-bold tracking-tight text-warm-ink sm:text-5xl">
              Every patient deserves a doctor who has read their chart.
            </h2>
            <div className="mt-8 space-y-5 text-[15px] leading-relaxed text-warm-muted">
              <p>
                Too often a visit starts cold. Labs go unread, prior imaging never surfaces, and history gets
                reconstructed from memory in the few minutes the schedule allows. People are left explaining
                themselves again and again, and important details slip through for reasons that have nothing to do
                with their health.
              </p>
              <p>
                We think every patient deserves a doctor who has actually read their chart — labs, imaging, history —
                and has the time to listen. When the AI doctor walks in already briefed and reasoning over a
                longitudinal record, the conversation gets clearer and the care gets better.
              </p>
              <p>
                And no patient should be harmed by an AI acting alone. The AI informs and triages; it never makes an
                autonomous diagnosis or prescription. A licensed clinician reviews and signs where required, and
                emergencies escalate to a human at once — so people get attention where it actually matters.
              </p>
            </div>
          </div>
        </Container>
      </section>

      {/* What we believe */}
      <section className="bg-warm-sand/40 py-24">
        <Container>
          <div className="max-w-2xl">
            <Eyebrow>What we believe</Eyebrow>
            <h2 className="mt-5 text-4xl font-bold tracking-tight text-warm-ink sm:text-5xl">
              A few things we hold to.
            </h2>
          </div>
          <div className="mt-14 grid gap-6 md:grid-cols-2">
            {VALUES.map((v) => (
              <div key={v.title} className="rounded-3xl border border-warm-border bg-warm-card p-7">
                <h3 className="text-xl font-semibold text-warm-ink">{v.title}</h3>
                <p className="mt-3 text-[15px] leading-relaxed text-warm-muted">{v.body}</p>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* How it actually works */}
      <section className="py-24">
        <Container>
          <div className="max-w-2xl">
            <Eyebrow>How it actually works</Eyebrow>
            <h2 className="mt-5 text-4xl font-bold tracking-tight text-warm-ink sm:text-5xl">
              Three steps, then a signed visit.
            </h2>
          </div>
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {STEPS.map((s) => (
              <div key={s.step} className="rounded-3xl border border-warm-border bg-warm-card p-7">
                <span className="font-display text-3xl italic text-warm-accent">{s.step}</span>
                <h3 className="mt-4 text-xl font-semibold text-warm-ink">{s.title}</h3>
                <p className="mt-3 text-[15px] leading-relaxed text-warm-muted">{s.body}</p>
              </div>
            ))}
          </div>
          <div className="mt-10">
            <MButton to="/platform" variant="outline">
              See the full platform <ArrowRight />
            </MButton>
          </div>
        </Container>
      </section>

      {/* Built on a modern stack */}
      <section className="bg-warm-sand/40 py-24">
        <Container>
          <div className="max-w-2xl">
            <Eyebrow>Under the hood</Eyebrow>
            <h2 className="mt-5 text-4xl font-bold tracking-tight text-warm-ink sm:text-5xl">
              Built on a modern stack.
            </h2>
            <p className="mt-6 text-[15px] leading-relaxed text-warm-muted">
              Powered by a handful of technologies we trust, each doing one thing well. We use these tools — no
              partnership or endorsement implied.
            </p>
          </div>
          <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {STACK.map((t) => (
              <div
                key={t.name}
                className="rounded-3xl border border-warm-border bg-warm-card px-6 py-5"
              >
                <div className="text-base font-semibold text-warm-ink">{t.name}</div>
                <div className="mt-1.5 text-sm leading-relaxed text-warm-muted">{t.what}</div>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* The team */}
      <section className="py-24">
        <Container>
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <div>
              <Eyebrow>The team</Eyebrow>
              <h2 className="mt-5 text-4xl font-bold tracking-tight text-warm-ink sm:text-5xl">
                Small, senior, fully remote.
              </h2>
              <p className="mt-6 text-[15px] leading-relaxed text-warm-muted">
                We're a tight, experienced team spread across time zones, united by a stubborn belief in calm
                software and safer care. We work async, hold a high bar, and ship the version we'd want to use
                ourselves.
              </p>
              <p className="mt-4 text-[15px] leading-relaxed text-warm-muted">
                If that sounds like your kind of place, we'd love to meet you.
              </p>
              <div className="mt-8">
                <MButton to="/careers">
                  See open roles <ArrowRight />
                </MButton>
              </div>
            </div>
            <div className="overflow-hidden rounded-4xl border border-warm-border shadow-[0_30px_80px_-30px_rgba(36,31,25,0.35)]">
              <img
                src={IMG.team}
                alt="A senior healthcare AI team collaborating beside a modern clinic"
                className="h-[320px] w-full object-cover sm:h-[440px]"
              />
            </div>
          </div>
        </Container>
      </section>

      {/* Closing CTA */}
      <section className="pb-24">
        <Container>
          <div className="rounded-4xl bg-warm-ink px-8 py-16 text-center text-warm-bg sm:px-16">
            <h2 className="mx-auto max-w-2xl text-4xl font-bold tracking-tight sm:text-5xl">
              Give every patient a doctor{" "}
              <span className="font-display italic font-normal text-warm-glow">who's read their chart</span>.
            </h2>
            <p className="mx-auto mt-5 max-w-xl text-[15px] leading-relaxed text-warm-bg/70">
              Launch your first AI doctor in minutes. Pay only for completed consultations — no seats, no contracts.
            </p>
            <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <MButton to="/signup">
                Start free <ArrowRight />
              </MButton>
              <MButton href="mailto:hello@wonderfulagent.ai" variant="outline">
                Talk to us
              </MButton>
            </div>
          </div>
        </Container>
      </section>
    </MarketingPage>
  );
}
